/*
 * Maison Avéla — mobile: make sure the FIRST tap on "ПРЕМИНАВАНЕ КЪМ
 * ПЛАЩАНЕ" after adding a product always reaches Econt, and that the
 * cart drawer never visually blocks Econt's interface.
 *
 * This file now fixes TWO separate, independently-confirmed problems.
 * Both were found by reading the real code involved (Econt's own public
 * script, this theme's own morph.js and cart-drawer.js) and by measuring
 * real DOM timing live on the unpublished theme — not by guessing.
 *
 * ============================================================================
 * PROBLEM 1 — native <dialog> top-layer stacking (mobile only)
 * ============================================================================
 * Below theme-drawer.js's own MODAL_BREAKPOINT (990px) the cart drawer is a
 * native <dialog> shown with showModal(), which places it in the browser's
 * "top layer" — a stacking context that always renders above ordinary DOM
 * content, no matter how high a z-index that content sets. Econt's checkout
 * interface is ordinary DOM content, so if the drawer <dialog> is still open
 * when Econt's popup appears, the popup renders underneath it.
 *
 * <theme-drawer>'s own close() is async (it waits for a CSS
 * animationend before calling the native panel.close() that actually
 * removes the <dialog> from the top layer — confirmed by reading
 * assets/theme-drawer.js's #closeDialog()). Left alone, nothing makes the
 * checkout click, or the button's native form submission, wait for that.
 *
 * Fix: close the native <dialog> directly and synchronously first —
 * dialog.close() is a plain synchronous DOM method, not gated on any
 * animation — then call the drawer's own close() on top of that (its
 * animation-wait branch is gated on `panel.open`, which is already false by
 * then, so it's skipped and the rest of the drawer's teardown runs
 * synchronously too). Both calls are the drawer's own public/native
 * methods; nothing is reimplemented.
 *
 * ============================================================================
 * PROBLEM 2 — Econt has not claimed the button yet on the FIRST render
 * ============================================================================
 * This is the problem that made "add product → immediately tap checkout"
 * still fail even after Problem 1 was fixed, and it is a different
 * mechanism from the top-layer issue above.
 *
 * Econt's own script (cdn.shopify.com/.../deliver-with-econt-31/assets/
 * econt-shipping-form.js — a public, unauthenticated static file, read
 * directly) does not attach its click handler to our server-rendered
 * button. It clones the button, strips every attribute except `class`,
 * marks the clone with data-econt-custom-button="true", binds its own
 * click handler to the CLONE only, and replaces the live button with it.
 * It finds buttons to clone via a MutationObserver on document.body; on
 * every relevant mutation it schedules that clone-and-replace through
 * `setTimeout(() => this._setupButtonListeners(), 500)` — a 500ms debounce
 * that is entirely inside Econt's own script, not something this theme
 * controls.
 *
 * When the cart is empty, `.cart__ctas` (the checkout button's wrapper)
 * does not exist in the DOM at all — confirmed live: with an empty cart,
 * `document.querySelector('.cart__ctas')` returns null; the drawer renders
 * `.cart-drawer--empty` markup instead. So the FIRST time a product is
 * added in a session, `.cart__ctas` and the native checkout button are
 * newly *created*, not updated — there is no previous Econt-claimed node
 * for morph.js to preserve (that is a separate case, already handled by
 * data-skip-subtree-update in snippets/cart-summary.liquid, which only
 * helps once a node already exists).
 *
 * Also confirmed live: cart-drawer.js's #handleCartLinesUpdate opens the
 * drawer optimistically, immediately on the add-to-cart event — before the
 * Section Rendering API response with the real cart content has even come
 * back. So the drawer is already open and its (still native, unclaimed)
 * checkout button is already visible and tappable before Econt has had any
 * chance to see it.
 *
 * Measured directly on this theme (MutationObserver timestamps around a
 * real first add-to-cart, cart previously empty):
 *   t=0ms      .cart__ctas is added to the DOM for the first time, with the
 *              plain native button (no data-econt-custom-button).
 *   t=+657ms   Econt's clone (data-econt-custom-button="true") replaces it.
 * For that ~650ms window the button in the DOM is the theme's own native
 * <button type="submit" form="cart-form">, with no click handler from Econt
 * at all. A tap that lands in this window does not reach Econt — it would
 * fall through to the button's native type="submit" form submission
 * instead, exactly matching "the first tap doesn't open Econt, a later one
 * does" once Econt has caught up.
 *
 * Fix: on a checkout-button click, check the one signal that actually
 * tells us whether Econt has claimed THIS button —
 * data-econt-custom-button. If it's already there, Econt's own listener is
 * bound and handles the click exactly as it always has; this code does
 * nothing further. If it is not there yet, this click is happening inside
 * the race window: prevent only the native form submission for this one
 * click (so it doesn't silently bypass Econt), and wait — via a
 * MutationObserver watching for that exact attribute, i.e. the real event
 * of Econt actually claiming the button, not a fixed delay — then replay a
 * plain click() on the now-claimed button. That replay is a full new click
 * dispatch, so it runs back through this very listener (this time finding
 * the marker already present, so it's a no-op here) and then bubbles to
 * Econt's own handler, completely unmodified. No Econt file, selector,
 * handler, or attribute is touched; no second checkout implementation is
 * created; nothing here ever runs unless the marker is genuinely absent.
 *
 * ============================================================================
 * Why 'click' only, capture phase, and no pointerdown/touchstart/etc.
 * ============================================================================
 * Econt's _setupButtonListeners() binds exactly one listener to its clone:
 *   clone.addEventListener("click", this._handleButtonClick.bind(this));
 * No pointerdown/pointerup/touchstart/touchend/mousedown/submit listener
 * exists anywhere in Econt's script. A 'click' listener is therefore the
 * correct and sufficient event to key off; there is no earlier event in
 * Econt's own handling to race against. An earlier version of this file
 * added a pointerdown listener speculatively, without this evidence — it
 * has been removed; adding one back would only add a real regression risk
 * (closing the drawer on a touch that starts on the button and drags into
 * a scroll) for no proven benefit.
 *
 * Capture phase, not bubble: confirmed by live testing that the theme's
 * own component event routing stops this click's bubbling before it
 * reaches document, so a bubble-phase document listener never runs.
 * Capture still does not intercept anything by default — no
 * preventDefault()/stopPropagation() for an already-claimed button — the
 * event continues on to Econt's own bubble-phase handler exactly as
 * before; this file only additionally closes the drawer first (Problem 1),
 * and only for a genuinely-unclaimed button, defers the native submission
 * until Econt has actually claimed it (Problem 2).
 *
 * Scoped automatically to real conditions rather than guessed viewport
 * widths: the dialog:modal check in Problem 1's fix is only true below the
 * 990px breakpoint, so desktop (non-modal sidebar drawer) is naturally
 * unaffected there. Problem 2's fix has no viewport gate at all — the
 * underlying race (Econt's 500ms debounce vs. a freshly-created button) is
 * not mobile-specific, it is just far more likely to be hit on mobile,
 * where the drawer opens full-screen and checkout is usually the very next
 * tap. On desktop, in the overwhelming majority of clicks the button is
 * already claimed by the time anyone clicks it, so this code is a no-op
 * there too.
 */
(() => {
  document.addEventListener(
    'click',
    (event) => {
      const checkoutButton = event.target instanceof Element ? event.target.closest('.cart__checkout-button') : null;
      if (!checkoutButton) return;

      // --- Problem 1: take the dialog out of the top layer immediately, if it's there. ---
      const modalDialog = checkoutButton.closest('dialog:modal');
      if (modalDialog) {
        const drawer = modalDialog.closest('theme-drawer');
        if (drawer && typeof drawer.close === 'function') {
          if (modalDialog.open) {
            modalDialog.close();
          }
          drawer.close();
        }
      }

      // --- Problem 2: has Econt actually claimed this exact button yet? ---
      if (checkoutButton.hasAttribute('data-econt-custom-button')) {
        // Already Econt's clone with Econt's own listener bound — let this
        // click continue to bubble to it exactly as it always has.
        return;
      }

      // Not claimed yet: this is the real race window measured above. Do not
      // let the native type="submit" action fire and silently bypass Econt.
      event.preventDefault();

      const container = checkoutButton.closest('.cart__ctas') || checkoutButton.parentElement;
      if (!container) return;

      const observer = new MutationObserver(() => {
        const claimedButton = container.querySelector('.cart__checkout-button[data-econt-custom-button]');
        if (!claimedButton) return;
        observer.disconnect();
        // A full new click dispatch: runs back through this same listener
        // (finds the marker, so Problem 2's branch is a no-op the second
        // time) and then bubbles to Econt's own handler, unmodified.
        claimedButton.click();
      });
      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-econt-custom-button'],
      });
    },
    true
  );
})();
